// Shared data layer of the «Трекер привычек» dashboard widgets.
// Widgets reference objects by sid (habitobject/logobject/... params of
// jsonwidgetparam), so the same widget rows work in every domain deployed
// from the mvp template regardless of numeric object ids.
import useCubus from '@/cubus/services/useCubus'
import Q from '@/cubus/services/Q'

// one event for the whole board: any widget that saved data emits it,
// the others reload themselves
export const HBT_REFRESH = 'cbs-habits-refresh'

export const HBT_COLORS = {
  txt: '#201e1d',
  mut: 'rgba(32,30,29,0.55)',
  div: 'rgba(32,30,29,0.4)',
  acc: '#ec3013',
  bg: '#f3f2f2',
  surf: '#eae9e9',
}

const sidOf = (param, key, fallback) => {
  const p = param && param[key]
  if (p && p.sid) return p.sid
  return fallback
}

export function hbtObjects(param) {
  return {
    habit: sidOf(param, 'habitobject', 'habit'),
    log: sidOf(param, 'logobject', 'habitlog'),
    type: sidOf(param, 'typeobject', 'habittype'),
    indicator: sidOf(param, 'indicatorobject', 'indicator'),
    measure: sidOf(param, 'measureobject', 'measurelog'),
  }
}

export function hbtParseDate(value) {
  if (!value) return null
  if (value instanceof Date) return value
  const d = new Date(String(value).replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? null : d
}

export function hbtDayKey(date) {
  const d = date instanceof Date ? date : hbtParseDate(date)
  if (!d) return null
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

export function hbtNowString() {
  const d = new Date()
  const p = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

export function hbtDaysAgoString(days) {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return `${hbtDayKey(d)} 00:00:00`
}

// «сегодня, 07:15» / «вчера, 21:30» / «25.07, 20:40»
export function hbtFmtAt(date) {
  const d = hbtParseDate(date)
  if (!d) return ''
  const p = n => String(n).padStart(2, '0')
  const time = `${p(d.getHours())}:${p(d.getMinutes())}`
  const key = hbtDayKey(d)
  const today = new Date()
  if (key === hbtDayKey(today)) return `сегодня, ${time}`
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  if (key === hbtDayKey(yesterday)) return `вчера, ${time}`
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}, ${time}`
}

const cellVal = (rec, sid) => (rec && rec[sid] ? rec[sid].value : null)
const cellTitle = (rec, sid) => (rec && rec[sid] ? rec[sid].title : null)

// habit catalog + habittype dictionary → sorted list of active habits
export async function hbtLoadHabits(vm, param) {
  const objs = hbtObjects(param)
  const typeQry = await useCubus.loadQuery(vm, {
    keymode: 'sid',
    objectsid: objs.type,
    filter: Q.and([Q.equals('del', false)]),
    limit: 100,
  })
  const typeById = {}
  if (typeQry && typeQry.recordset) {
    typeQry.recordset.records.forEach(r => {
      typeById[Number(cellVal(r, 'id'))] = cellVal(r, 'sid')
    })
  }
  const qry = await useCubus.loadQuery(vm, {
    keymode: 'sid',
    objectsid: objs.habit,
    filter: Q.and([Q.equals('del', false)]),
    limit: 500,
  })
  const habits = []
  if (qry && qry.recordset) {
    qry.recordset.records.forEach(r => {
      if (cellVal(r, 'isactive') === false) return
      if (cellVal(r, 'isfolder') === true) return
      habits.push({
        id: Number(cellVal(r, 'id')),
        sid: cellVal(r, 'sid'),
        name: cellTitle(r, 'name') || cellVal(r, 'name'),
        en: cellVal(r, 'descr') || '',
        type: typeById[Number(cellVal(r, 'habittype'))] || 'count',
        unit: cellVal(r, 'unitname') || 'раз',
        plan: Number(cellVal(r, 'planday')) || 0,
        def: Number(cellVal(r, 'defaultset')) || 0,
        ordernum: Number(cellVal(r, 'ordernum')) || 0,
        record: r,
      })
    })
  }
  habits.sort((a, b) => a.ordernum - b.ordernum || a.id - b.id)
  return habits
}

// habit log for the last N days → [{id, habitId, date, day, reps}]
export async function hbtLoadLogs(vm, param, days) {
  const objs = hbtObjects(param)
  const qry = await useCubus.loadQuery(vm, {
    keymode: 'sid',
    objectsid: objs.log,
    filter: Q.and([Q.equals('del', false), Q.geq('date', hbtDaysAgoString(days))]),
    limit: 5000,
  })
  const logs = []
  if (qry && qry.recordset) {
    qry.recordset.records.forEach(r => {
      const date = hbtParseDate(cellVal(r, 'date'))
      if (!date) return
      logs.push({
        id: Number(cellVal(r, 'id')),
        habitId: Number(cellVal(r, 'habit')),
        date,
        day: hbtDayKey(date),
        reps: Number(cellVal(r, 'reps')) || 0,
      })
    })
  }
  return logs
}

// measurements for the last N days (optionally one indicator) sorted by date desc
export async function hbtLoadMeasures(vm, param, days, indicatorId) {
  const objs = hbtObjects(param)
  const conds = [Q.equals('del', false), Q.geq('date', hbtDaysAgoString(days))]
  if (indicatorId) conds.push(Q.equals('indicator', indicatorId))
  const qry = await useCubus.loadQuery(vm, {
    keymode: 'sid',
    objectsid: objs.measure,
    filter: Q.and(conds),
    limit: 5000,
  })
  const rows = []
  if (qry && qry.recordset) {
    qry.recordset.records.forEach(r => {
      const date = hbtParseDate(cellVal(r, 'date'))
      if (!date) return
      rows.push({
        id: Number(cellVal(r, 'id')),
        indicatorId: Number(cellVal(r, 'indicator')),
        date,
        day: hbtDayKey(date),
        value: cellVal(r, 'mvalue') === null ? null : Number(cellVal(r, 'mvalue')),
        value2: cellVal(r, 'mvalue2') === null ? null : Number(cellVal(r, 'mvalue2')),
      })
    })
  }
  rows.sort((a, b) => b.date - a.date)
  return rows
}

// indicator catalog → sorted list
export async function hbtLoadIndicators(vm, param) {
  const objs = hbtObjects(param)
  const qry = await useCubus.loadQuery(vm, {
    keymode: 'sid',
    objectsid: objs.indicator,
    filter: Q.and([Q.equals('del', false)]),
    limit: 100,
  })
  const rows = []
  if (qry && qry.recordset) {
    qry.recordset.records.forEach(r => {
      if (cellVal(r, 'isactive') === false) return
      if (cellVal(r, 'isfolder') === true) return
      rows.push({
        id: Number(cellVal(r, 'id')),
        sid: cellVal(r, 'sid'),
        name: cellTitle(r, 'name') || cellVal(r, 'name'),
        en: cellVal(r, 'descr') || '',
        unit: cellVal(r, 'unitname') || '',
        warn: cellVal(r, 'warnlevel') === null ? null : Number(cellVal(r, 'warnlevel')),
        risk: cellVal(r, 'risklevel') === null ? null : Number(cellVal(r, 'risklevel')),
        goal: cellVal(r, 'goallevel') === null ? null : Number(cellVal(r, 'goallevel')),
        ordernum: Number(cellVal(r, 'ordernum')) || 0,
      })
    })
  }
  rows.sort((a, b) => a.ordernum - b.ordernum || a.id - b.id)
  return rows
}

// day completion percent of one habit given its logged total
export function hbtHabitDayPct(habit, total) {
  if (habit.type === 'check') return total > 0 ? 100 : 0
  if (habit.plan > 0) return Math.min(100, Math.round((total / habit.plan) * 100))
  return total > 0 ? 100 : 0
}

// completion of one day: check habit → done/not, count habit → capped total/plan
export function hbtDayCompletion(habits, logs, dayKey) {
  const perHabit = {}
  let units = 0
  let done = 0
  habits.forEach(h => {
    if (h.type === 'measure') return
    const total = logs
      .filter(l => l.habitId === h.id && l.day === dayKey)
      .reduce((a, l) => a + l.reps, 0)
    const pct = hbtHabitDayPct(h, total)
    perHabit[h.id] = { total, pct, done: pct >= 100 }
    units += 1
    done += pct / 100
  })
  return {
    units,
    done,
    pct: units > 0 ? Math.round((done / units) * 100) : 0,
    perHabit,
  }
}

// days in a row with the plan fully closed; today counts only when closed
export function hbtStreak(habits, logs, maxDays) {
  let streak = 0
  const day = new Date()
  const today = hbtDayCompletion(habits, logs, hbtDayKey(day))
  if (today.units > 0 && today.pct >= 100) streak += 1
  for (let i = 1; i <= maxDays; i += 1) {
    day.setDate(day.getDate() - 1)
    const c = hbtDayCompletion(habits, logs, hbtDayKey(day))
    if (c.units === 0 || c.pct < 100) break
    streak += 1
  }
  return streak
}

// ok / warn / risk zone of a measurement against indicator thresholds
export function hbtZone(indicator, value) {
  if (value === null || value === undefined) return 'ok'
  if (indicator.risk !== null && value >= indicator.risk) return 'risk'
  if (indicator.warn !== null && value >= indicator.warn) return 'warn'
  return 'ok'
}

export const HBT_ZONES = {
  ok: {
    label: 'Норма', bg: 'transparent', color: '#201e1d', border: 'rgba(32,30,29,0.4)',
  },
  warn: {
    label: 'Внимание', bg: '#ffe0d9', color: '#7c1405', border: '#ffc4b8',
  },
  risk: {
    label: 'Риск', bg: '#ec3013', color: '#f3f2f2', border: '#ec3013',
  },
}

// design's month-grid cell palette by completion percent
export function hbtCellStyle(pct) {
  if (pct >= 100) return { background: '#ec3013', color: '#f3f2f2', borderColor: '#ec3013' }
  if (pct >= 70) return { background: '#ff9783', color: '#4d170e', borderColor: '#ff9783' }
  if (pct > 0) return { background: '#ffe0d9', color: '#7c1405', borderColor: '#ffc4b8' }
  return { background: 'transparent', color: 'rgba(32,30,29,0.45)', borderColor: 'rgba(32,30,29,0.4)' }
}

// insert one log record: habit ref + reps, date = now
export async function hbtAddLog(vm, param, habit, reps) {
  const objs = hbtObjects(param)
  const res = await useCubus.loadNewRecord(vm, { keymode: 'sid', objectsid: objs.log })
  if (!res || !res.record || !res.record.record) return null
  const { record } = res.record
  record.habit = { value: habit.id, title: habit.name, status: 'changed' }
  record.reps = { value: reps, title: null, status: 'changed' }
  record.date = { value: hbtNowString(), title: null, status: 'changed' }
  return useCubus.saveRecord(vm, { keymode: 'sid', objectsid: objs.log, record })
}

// insert one measurement record
export async function hbtAddMeasure(vm, param, indicator, value, value2) {
  const objs = hbtObjects(param)
  const res = await useCubus.loadNewRecord(vm, { keymode: 'sid', objectsid: objs.measure })
  if (!res || !res.record || !res.record.record) return null
  const { record } = res.record
  record.indicator = { value: indicator.id, title: indicator.name, status: 'changed' }
  record.mvalue = { value, title: null, status: 'changed' }
  if (value2 !== undefined && value2 !== null) {
    record.mvalue2 = { value: value2, title: null, status: 'changed' }
  }
  record.date = { value: hbtNowString(), title: null, status: 'changed' }
  return useCubus.saveRecord(vm, { keymode: 'sid', objectsid: objs.measure, record })
}

// soft-delete today's log records of one habit (uncheck a check habit)
export async function hbtDeleteTodayLogs(vm, param, habitId, logs) {
  const objs = hbtObjects(param)
  const today = hbtDayKey(new Date())
  const targets = logs.filter(l => l.habitId === habitId && l.day === today)
  /* eslint-disable no-await-in-loop, no-restricted-syntax */
  for (const t of targets) {
    await useCubus.delRecord(vm, {
      objectsid: objs.log,
      record: { id: { value: t.id } },
    })
  }
  /* eslint-enable no-await-in-loop, no-restricted-syntax */
  return targets.length
}

// change the daily plan stored on the habit catalog record
export async function hbtSavePlan(vm, param, habit, plan) {
  const objs = hbtObjects(param)
  const record = JSON.parse(JSON.stringify(habit.record))
  record.planday = { value: plan, title: null, status: 'changed' }
  return useCubus.saveRecord(vm, { keymode: 'sid', objectsid: objs.habit, record })
}

const RU_DAYS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
const RU_MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
const EN_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

export function hbtFmtDateRu(d) {
  return `${RU_DAYS[d.getDay()]}, ${d.getDate()} ${RU_MONTHS[d.getMonth()]}`
}

export function hbtFmtDateEn(d) {
  return `${EN_DAYS[d.getDay()]}, ${d.getDate()} ${EN_MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

export function hbtWeekDayShort(d) {
  return RU_DAYS[d.getDay()]
}
